import { Injectable } from '@angular/core';
import {
    requestSearchResult,
    requestSearchResultElectronics,
    updateSearchError,
    updateSearchHistory,
} from '@ecs-stores/search/search.actions';
import {
    selectElectronicsTabSearchError,
    selectElectronicsTabSearchTerm,
    selectOrderNumberSearchResult,
    selectSearchError,
    selectSearchHistory,
    selectSearchResult,
    selectSearchTerm,
    selectSearchTermAndErrorsAndResult,
} from '@ecs-stores/search/search.selectors';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { OrderNumberSearchResult } from '../../features/result/spare-parts-result/components/order-number-table/order-number-table.interface';
import { SearchResultFilter, isNullOr, splitSearchString } from './search.helper';
import { SearchError, SearchResult } from './search.interface';

@Injectable({
    providedIn: 'root',
})
export class SearchStoreService {
    public constructor(private store: Store) {}

    public search(searchStr: string) {
        const searchTerm = splitSearchString(searchStr).map((term) => term.trim());
        this.store.dispatch(requestSearchResult({ payload: { searchTerm } }));
    }

    public electronicsTabSearch(searchStr: string) {
        const searchTerm = splitSearchString(searchStr).map((term) => term.trim());
        this.store.dispatch(requestSearchResultElectronics({ payload: { searchTerm } }));
    }

    public fetchSearchHistory() {
        // TODO: replace with new action for initial fetch?
        this.store.dispatch(updateSearchHistory({ payload: { searchHistory: [] }, initial: true }));
    }

    public updateSearchError(searchError: SearchError | null) {
        this.store.dispatch(updateSearchError({ payload: { searchError } }));
    }

    public getSearchTerm(): Observable<string[]> {
        return this.store.select(selectSearchTerm);
    }

    public getElectronicsTabSearchTerm(): Observable<string[]> {
        return this.store.select(selectElectronicsTabSearchTerm);
    }

    /**
     * Get the current search result state from the store.
     *
     * Use `resultFilter` to only get those results matching your filter function.
     *
     * Set `emitNull` to emit `null` result values (even if these don't match your filter function).
     *
     * @param resultFilter Filter function to apply to result in order to only receive relevant results
     * @param emitNull Whether or not to also emit `null` value results
     * @returns The stored search result (with `resultFilter` set: only results matching the filter)
     */
    public getSearchResult<T extends SearchResult>(
        resultFilter: SearchResultFilter<T>,
        emitNull: true
    ): Observable<T | null>;
    public getSearchResult<T extends SearchResult>(
        resultFilter: SearchResultFilter<T>,
        emitNull?: false
    ): Observable<T>;
    public getSearchResult(): Observable<SearchResult>;
    public getSearchResult<T extends SearchResult>(
        resultFilter?: SearchResultFilter<T>,
        emitNull?: boolean
    ): Observable<SearchResult | T | null> {
        const result = this.store.select(selectSearchResult);

        if (!resultFilter) {
            return result;
        }

        return emitNull ? result.pipe(filter(isNullOr(resultFilter))) : result.pipe(filter(resultFilter));
    }

    /**
     * Returns partial SearchState
     *
     * @returns searchResult, searcherm, orderNumberSearchResult and searchError
     */
    public getSearchTermAndErrorsAndResult(): Observable<{
        searchResult: SearchResult;
        searchTerm: string[];
        orderNumberSearchResult: OrderNumberSearchResult | null | undefined;
        searchError: SearchError | null | undefined;
        electronicsTabSearchError: SearchError | null | undefined;
    }> {
        return this.store.select(selectSearchTermAndErrorsAndResult);
    }

    public getOrderNumberSearchResult(): Observable<OrderNumberSearchResult | null | undefined> {
        return this.store.select(selectOrderNumberSearchResult);
    }

    public getSearchHistory(): Observable<string[]> {
        return this.store.select(selectSearchHistory);
    }

    public getSearchError(): Observable<SearchError | null | undefined> {
        return this.store.select(selectSearchError);
    }

    public getElectronicsTabSearchError(): Observable<SearchError | null | undefined> {
        return this.store.select(selectElectronicsTabSearchError);
    }
}
